<template>
  <div class="text-content">
    <div class="full-width-container">
      <!-- 文案生成列表 -->
      <div class="text-list">
        <!-- 新生成的项目（优先显示） -->
        <div class="text-item" v-for="(item, index) in newTextItems" :key="'new-' + index">
          <!-- 模型名称和时间 -->
          <div class="text-header">
            <div class="text-info-badges">
              <div class="model-badge">
                <i class="icon">⚙️</i> {{ item.model }}
              </div>
              <div class="info-badge">
                {{ item.wordCount }}
              </div>
              <div class="info-badge">
                {{ item.style }}
              </div>
              <div class="type-badge">
                {{ item.type }}
              </div>
            </div>
            <div class="generate-time">
              {{ item.generateTime }}
            </div>
          </div>
          
          <!-- 文案内容布局 -->
          <div class="text-content-layout">
            <div class="text-section">
              <div class="media-box">
                <div class="text-content-box">
                  <!-- 加载状态 -->
                  <div v-if="item.isLoading" class="loading-container">
                    <div class="glass-effect">
                      <div class="glass-shine"></div>
                    </div>
                    <div class="loading-content">
                      <div class="loading-spinner"></div>
                      <div class="loading-text">{{ item.statusText }}</div>
                      <div class="progress-bar-container">
                        <div class="progress-bar">
                          <div class="progress-fill" :style="{ width: item.progress + '%' }"></div>
                        </div>
                        <span class="progress-text">{{ Math.round(item.progress) }}%</span>
                      </div>
                    </div>
                  </div>
                  <!-- 生成完成 -->
                  <div v-else class="text-result">{{ item.content }}</div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 提示词和功能按钮区域 -->
          <div class="prompt-container">
            <div class="prompt-content">
              <div class="prompt">{{ item.prompt }}</div>
            </div>
            <div class="action-buttons">
              <button class="action-btn" @click="copyText(item.content)" title="复制文案">
                <i class="icon">📋</i>
              </button>
              <button class="action-btn" @click="regenerateText(index, true)" title="重新生成">
                <i class="icon">🔄</i>
              </button>
              <button class="action-btn" @click="editText(index, true)" title="编辑">
                <i class="icon">✏️</i>
              </button>
              <button class="action-btn delete" @click="deleteText(index, true)" title="删除">
                <i class="icon">🗑️</i>
              </button>
            </div>
          </div>
        </div>
        
        <!-- 历史生成项目（后置加载） -->
        <div class="text-item" v-for="(item, index) in historyTextItems" :key="'history-' + item.id">
          <!-- 模型名称和时间 -->
          <div class="text-header">
            <div class="text-info-badges">
              <div class="model-badge">
                <i class="icon">⚙️</i> {{ item.model }}
              </div>
              <div class="info-badge">
                {{ item.wordCount }}
              </div>
              <div class="info-badge">
                {{ item.style }}
              </div>
              <div class="type-badge">
                {{ item.type }}
              </div>
            </div>
            <div class="generate-time">
              {{ item.generateTime }}
            </div>
          </div>
          
          <!-- 文案内容布局 -->
          <div class="text-content-layout">
            <div class="text-section">
              <div class="media-box">
                <div class="text-content-box">
                  <!-- 生成完成 -->
                  <div class="text-result">{{ item.content }}</div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 提示词和功能按钮区域 -->
          <div class="prompt-container">
            <div class="prompt-content">
              <div class="prompt">{{ item.prompt }}</div>
            </div>
            <div class="action-buttons">
              <button class="action-btn" @click="copyText(item.content)" title="复制文案">
                <i class="icon">📋</i>
              </button>
              <button class="action-btn" @click="regenerateText(index, false)" title="重新生成">
                <i class="icon">🔄</i>
              </button>
              <button class="action-btn" @click="editText(index, false)" title="编辑">
                <i class="icon">✏️</i>
              </button>
              <button class="action-btn delete" @click="deleteText(index, false)" title="删除">
                <i class="icon">🗑️</i>
              </button>
            </div>
          </div>
        </div>
        
        <!-- 空状态 -->
        <div v-if="newTextItems.length === 0 && historyTextItems.length === 0 && !isLoadingHistory" class="empty-state">
          <div class="empty-icon">📄</div>
          <h3>还没有生成过文案</h3>
          <p>使用下面的工具栏开始生成您的第一条文案吧！</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import historyCache from '@/utils/historyCache'

export default {
  name: 'TextContent',
  props: {
    params: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      newTextItems: [],
      historyTextItems: [],
      isLoadingHistory: false,
      isLoaded: false
    }
  },
  mounted() {
    this.loadHistory()
  },
  watch: {
    params: {
      handler(newParams) {
        // 只是接收参数，不主动生成，让 TextToolbar 负责生成
        console.log('=== TextContent 接收参数 ===', newParams)
      },
      deep: true
    }
  },
  methods: {
    async loadHistory() {
      if (this.isLoaded) return
      this.isLoaded = true
      
      // 延迟加载历史数据，让新生成的内容优先显示
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // 检查缓存是否有效
      if (historyCache.isCacheValid() && historyCache.hasModuleData('text')) {
        console.log('使用缓存的文案历史数据')
        const cachedData = historyCache.getModuleHistory('text')
        this.historyTextItems = this.formatHistoryData(cachedData)
        return
      }
      
      this.isLoadingHistory = true
      
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const user_id = userInfo.id || 0
      
      try {
        const response = await axios.get('/api/text-history', {
          params: { user_id: user_id }
        })
        
        // 处理响应数据 - 可能是直接数组或包装在响应中
        let data = response.data
        if (data && data.data) {
          data = data.data
        }
        
        // 确保 data 是数组
        if (!Array.isArray(data)) {
          data = []
        }
        
        // 更新缓存
        historyCache.updateModuleHistory('text', data)
        
        // 将后端数据格式转换为前端期望的格式，存入历史列表
        this.historyTextItems = this.formatHistoryData(data)
      } catch (error) {
        console.error('加载文案历史失败:', error)
        this.historyTextItems = []
      } finally {
        this.isLoadingHistory = false
      }
    },
    
    formatHistoryData(data) {
      return data.map(item => ({
        id: item.id,
        type: item.type || '通用文案',
        prompt: item.topic || '',
        model: item.model || '',
        wordCount: item.length || '',
        style: item.style || '',
        content: item.content || '',
        generateTime: item.createdAt || item.created_at || '',
        isLoading: false,
        progress: 0,
        statusText: ''
      }))
    },
    getCurrentTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    copyText(content) {
      navigator.clipboard.writeText(content).then(() => {
        alert('文案已复制到剪贴板')
      })
    },
    regenerateText(index, isNew) {
      const item = isNew ? this.newTextItems[index] : this.historyTextItems[index]
      console.log('重新生成:', item)
    },
    editText(index, isNew) {
      const item = isNew ? this.newTextItems[index] : this.historyTextItems[index]
      console.log('编辑:', item)
    },
    async deleteText(index, isNew) {
      if (!confirm('确定要删除这条文案吗？')) {
        return
      }
      
      try {
        if (isNew) {
          // 新生成的没有数据库ID，直接移除
          this.newTextItems.splice(index, 1)
          console.log('✅ 已删除新生成的文案')
        } else {
          // 历史记录，先从数据库删除
          const item = this.historyTextItems[index]
          console.log('🗑️ 准备删除:', item)
          
          if (item.id) {
            const response = await fetch(`/api/text-history/${item.id}`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' }
            })
            
            const data = await response.json()
            console.log('📤 删除响应:', data)
            
            if (data.code === 200) {
              console.log('✅ 数据库删除成功')
            } else {
              console.warn('⚠️ 数据库删除失败，但继续移除前端显示')
            }
          }
          
          this.historyTextItems.splice(index, 1)
        }
      } catch (error) {
        console.error('❌ 删除失败:', error)
        alert('删除失败，请重试')
      }
    },
    addNewText(newText) {
      this.newTextItems.unshift(newText)
    },
    updateProgress(updatedItem) {
      console.log('📢 TextContent 收到更新:', updatedItem)
      const index = this.newTextItems.findIndex(item => item.id === updatedItem.id)
      if (index !== -1) {
        this.$set(this.newTextItems, index, { ...this.newTextItems[index], ...updatedItem })
        console.log('✅ 更新成功')
      } else {
        console.log('❌ 未找到对应的项')
      }
    }
  }
}
</script>

<style scoped>
.text-content {
  width: 100%;
}

.full-width-container {
  width: 100%;
  box-sizing: border-box;
  max-width: 1600px;
  margin: 0 auto;
  padding-right: 50px;
  overflow: hidden;
}

/* 文案列表 */
.text-list {
  width: 100%;
  margin-bottom: 120px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.text-item {
  background: rgba(255, 255, 255, 0.03);
  border: none;
  border-radius: 16px;
  padding: 16px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  height: 66.67vh;
  min-height: 500px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.text-item:hover {
  box-shadow: 0 8px 32px rgba(0, 230, 172, 0.1);
}

.text-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: nowrap;
  width: 100%;
  box-sizing: border-box;
}

.text-info-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.model-badge {
  background: rgba(0, 230, 172, 0.1);
  border: 1px solid rgba(0, 230, 172, 0.3);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #00e6ac;
  display: flex;
  align-items: center;
  gap: 6px;
}

.type-badge {
  background: rgba(0, 230, 172, 0.1);
  border: 1px solid rgba(0, 230, 172, 0.3);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #00e6ac;
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-badge {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #e6e6e6;
  display: flex;
  align-items: center;
  gap: 6px;
}

.generate-time {
  font-size: 12px;
  color: #aaa;
  white-space: nowrap;
  margin-left: 10px;
}

/* 文案内容布局 */
.text-content-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.text-section {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  box-sizing: border-box;
}

.media-box {
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.02);
  border: none;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  position: relative;
  padding: 0;
}

.text-content-box {
  width: 100%;
  height: 100%;
  padding: 16px;
  box-sizing: border-box;
  overflow-y: auto;
}

.text-result {
  color: #e6f1ff;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  text-align: left;
  padding: 0;
  margin: 0;
}

/* 提示词容器 */
.prompt-container {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.prompt-content {
  flex: 1;
  min-width: 0;
}

.prompt-container .prompt {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: #e6e6e6;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 12px;
  color: #aaa;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(0, 230, 172, 0.3);
  color: #00e6ac;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 230, 172, 0.2);
}



.action-btn .icon {
  font-size: 16px;
}

/* 加载容器 */
.loading-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
}

/* 玻璃效果 */
.glass-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(0, 230, 172, 0.05) 0%,
    rgba(0, 230, 172, 0.1) 50%,
    rgba(0, 230, 172, 0.05) 100%
  );
  backdrop-filter: blur(10px);
}

/* 玻璃反光动画 */
.glass-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 70%
  );
  animation: shine 2s infinite;
  transform: rotate(45deg);
}

@keyframes shine {
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) rotate(45deg);
  }
}

/* 加载内容 */
.loading-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
}

/* 加载动画 */
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 230, 172, 0.2);
  border-top-color: #00e6ac;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 加载文字 */
.loading-text {
  color: #00e6ac;
  font-size: 14px;
  font-weight: 500;
}

/* 进度条容器 */
.progress-bar-container {
  width: 100%;
  max-width: 200px;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 进度条 */
.progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

/* 进度填充 */
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00e6ac, #00cc99);
  border-radius: 3px;
  transition: width 0.2s ease;
  position: relative;
  overflow: hidden;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* 进度文字 */
.progress-text {
  color: #888;
  font-size: 14px;
  min-width: 40px;
  text-align: right;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #8892b0;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  margin-bottom: 8px;
  color: #e6f1ff;
}

.empty-state p {
  font-size: 16px;
  max-width: 400px;
  margin: 0 auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .text-list {
    padding: 0 20px;
    margin-bottom: 200px;
  }
  
  .text-item {
    padding: 16px;
    height: auto;
    min-height: auto;
    max-height: none;
  }
  
  .prompt-container {
    flex-direction: column;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: center;
  }
}
</style>